import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Loader2, X } from "lucide-react";
import { api } from "../../lib/api";
import { trackEvent } from "../../lib/analytics";
import { setActiveVehicle } from "../../lib/fitment";

// Tiny in-memory cache of common queries (cleared on reload).
const _cache = new Map();

/**
 * Vehicle-first autocomplete. Type a vehicle (e.g. "2018 Ford F650",
 * "Ferrari F8") and jump straight to its exact-fit parts page.
 * Reuses the existing /vehicle/:id?label= navigation contract.
 */
export default function VehicleSearch({ autoFocus = false, onNavigate }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);

  const navigate = useNavigate();
  const boxRef = useRef(null);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);
  const reqIdRef = useRef(0);

  useEffect(() => {
    if (autoFocus && inputRef.current) inputRef.current.focus();
  }, [autoFocus]);

  const runSearch = useCallback((value) => {
    const term = value.trim();
    if (term.replace(/[^a-z0-9]/gi, "").length < 2) {
      setResults([]);
      setLoading(false);
      setOpen(false);
      return;
    }
    if (_cache.has(term.toLowerCase())) {
      setResults(_cache.get(term.toLowerCase()));
      setOpen(true);
      setLoading(false);
      return;
    }
    setLoading(true);
    const myReq = ++reqIdRef.current;
    api
      .get(`/vehicles/search?q=${encodeURIComponent(term)}`)
      .then(({ data }) => {
        if (myReq !== reqIdRef.current) return; // stale response
        const list = data.results || [];
        _cache.set(term.toLowerCase(), list);
        setResults(list);
        setOpen(true);
        setActive(-1);
        trackEvent("vehicle_search", { search_term: term, result_count: list.length });
      })
      .catch(() => {
        if (myReq === reqIdRef.current) setResults([]);
      })
      .finally(() => {
        if (myReq === reqIdRef.current) setLoading(false);
      });
  }, []);

  const onChange = (e) => {
    const value = e.target.value;
    setQ(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(value), 280);
  };

  const select = (r) => {
    if (!r) return;
    trackEvent("vehicle_select", {
      vehicle_label: r.label,
      vehicle_id: r.vehicle_id,
      product_count: r.product_count,
    });
    setOpen(false);
    setQ("");
    setResults([]);
    setActiveVehicle({
      vehicle_id: r.vehicle_id,
      label: r.label,
      year: r.year,
      make: r.make,
      model: r.model,
      trim: r.trim || "",
    });
    if (onNavigate) onNavigate();
    navigate(`/vehicle/${r.vehicle_id}?label=${encodeURIComponent(r.label)}`);
  };

  const onKeyDown = (e) => {
    if (!open || results.length === 0) {
      if (e.key === "Escape") onNavigate?.();
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      select(results[active >= 0 ? active : 0]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  // Close when clicking outside.
  useEffect(() => {
    const onDoc = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  return (
    <div ref={boxRef} className="relative w-full" data-testid="vehicle-search">
      <div className="relative">
        <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none" />
        <input
          ref={inputRef}
          data-testid="vehicle-search-input"
          type="text"
          value={q}
          onChange={onChange}
          onKeyDown={onKeyDown}
          onFocus={() => results.length > 0 && setOpen(true)}
          placeholder="Search your vehicle — e.g. 2018 Ford F650, Ferrari F8"
          autoComplete="off"
          aria-label="Search your vehicle"
          className="w-full h-12 pl-12 pr-11 bg-white/5 border border-white/15 text-white text-sm rounded-md focus:outline-none focus:border-red-500 transition placeholder:text-zinc-500"
        />
        <div className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500">
          {loading ? (
            <Loader2 size={16} className="animate-spin" />
          ) : q ? (
            <button
              type="button"
              data-testid="vehicle-search-clear"
              onClick={() => { setQ(""); setResults([]); setOpen(false); inputRef.current?.focus(); }}
              className="hover:text-white transition"
              aria-label="Clear search"
            >
              <X size={16} />
            </button>
          ) : null}
        </div>
      </div>

      {open && (
        <div
          data-testid="vehicle-search-results"
          className="absolute left-0 right-0 mt-2 bg-[#0d0d0d] border border-white/10 rounded-md shadow-2xl overflow-hidden z-50 max-h-[60vh] overflow-y-auto"
        >
          {results.length === 0 && !loading ? (
            <div className="px-4 py-5 text-sm text-zinc-500" data-testid="vehicle-search-empty">
              No vehicles found. Try a year, make or model.
            </div>
          ) : (
            results.map((r, i) => (
              <button
                key={r.vehicle_id + r.label}
                type="button"
                data-testid={`vehicle-search-result-${i}`}
                onMouseEnter={() => setActive(i)}
                onClick={() => select(r)}
                className={`w-full text-left px-4 py-3 flex items-center justify-between gap-4 border-b border-white/5 last:border-b-0 transition ${
                  active === i ? "bg-white/10" : "hover:bg-white/5"
                }`}
              >
                <span className="text-sm text-white font-medium truncate">{r.label}</span>
                <span className="shrink-0 text-[11px] uppercase tracking-[0.15em] text-emerald-500 font-mono">
                  {r.product_count} {r.product_count === 1 ? "part" : "parts"}
                </span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
